import { useCallback, useContext, useState } from 'react';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  WAITING_ROOM_REQUEST_TYPE,
  WaitingRoomRequest,
} from 'models/WaitingRoom';
import dataRoomApi from '../../../dataRoomApi';
import { ApproveSecureLinkRequestPayload } from '../../../dataRoomApi/waiting-room';
import { useInvalidateWaitingRoom } from '../../../hooks/useWaitingRoom';
import DataRoomContext from '../../../providers/DataRoom/DataRoom.context';

// Which route approves which request, and whether the roles the drawer collects
// are part of it. Handing over a password does not let anyone new in, so it
// takes no roles.
const APPROVE_REQUEST: Record<
  string,
  (requestId: string, data: ApproveSecureLinkRequestPayload) => Promise<unknown>
> = {
  [WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_ACCESS]:
    dataRoomApi.waitingRoom.approveSecureLinkAccess,
  [WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_EXPIRED]:
    dataRoomApi.waitingRoom.extendSecureLinkAccess,
  [WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_PASSWORD]: (requestId) =>
    dataRoomApi.waitingRoom.sendSecureLinkPassword(requestId),
};

export default function useSecureLinkRequestReview() {
  const [isLoading, setIsLoading] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);
  const { dataRoom } = useContext(DataRoomContext);

  // Every outcome takes the row out of the queue, so the table and the
  // navigation badge are refetched here rather than by each caller.
  const invalidateWaitingRoom = useInvalidateWaitingRoom(dataRoom?._id);

  const approveRequest = useCallback(
    async (
      { _id, type }: WaitingRoomRequest,
      values: ApproveSecureLinkRequestPayload,
      onSuccess?: () => void,
    ) => {
      const approve = APPROVE_REQUEST[type];

      if (!approve) return;

      try {
        setIsLoading(true);

        await approve(_id, values);

        invalidateWaitingRoom();
        onSuccess?.();
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [invalidateWaitingRoom, popServerError],
  );

  // One route for all three types: what the request was asking for stops
  // mattering the moment it is turned down.
  const declineRequest = useCallback(
    async (requestId: string, onSuccess?: () => void) => {
      try {
        setIsLoading(true);

        await dataRoomApi.waitingRoom.declineSecureLinkRequest(requestId);

        invalidateWaitingRoom();
        onSuccess?.();
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [invalidateWaitingRoom, popServerError],
  );

  return { isLoading, approveRequest, declineRequest };
}
